"use client";

import communityGraphic1 from "@/app/(public)/shared/assets/graphics/community-1.jpg";
import communityGraphic2 from "@/app/(public)/shared/assets/graphics/community-2.jpg";
import communityGraphic3 from "@/app/(public)/shared/assets/graphics/community-3.webp";
import { Button } from "@/app/(public)/shared/ui/Button";
import { StringInput, WholeNumberInput } from "@/components/ui/shared/SpecialInput";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { motion } from "framer-motion";
import { Building, Calendar, Lock, MapPin, MessageSquare, Star, Users, Wrench } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export default function Community() {
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    planning_experience: "",
    linkedin: "",
    venues_toured: 0,
    events_planned: 0,
    willing_to_review: 0,
  });

  // Form validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const features = [
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Curated Venue Intelligence",
      description:
        "Tired of outdated info or useless Google reviews? See detailed notes from actual corporate events—WiFi quality, staff responsiveness, layout quirks, and more.",
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Private or Shared Reviews",
      description:
        "Control who sees your notes. Keep them private for your team or share them with the broader community to help others.",
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Tools for Power Planners",
      description: "Upload your own venue spreadsheet and we'll help clean, match, and enrich it with our database.",
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Real Conversations, Not Sales Pitches",
      description:
        "Join our invite-only Slack group for vetted planners to discuss challenges, swap ideas, and recommend trusted venues and partners.",
    },
  ];

  const targetAudience = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Internal event planners and People Ops teams",
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: "Executive assistants managing complex events",
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "External agencies and operators who want to scale their venue knowledge",
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: 'Anyone who\'s ever said: "I wish I had known that before we booked."',
    },
  ];

  const supabase = createClient();
  const router = useRouter();

  // Auto-populate user info if logged in
  useEffect(() => {
    (async () => {
      const user = await getCurrentUser(supabase);
      if (user) {
        setFormData((prev) => ({
          ...prev,
          name: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : prev.name,
          email: user.email || prev.email,
          company: user.company || prev.company,
          role: user.title || prev.role,
        }));
      }
    })();
  }, []);

  // Validation function
  const validateForm = () => {
    const errors: Record<string, boolean> = {};

    if (!formData.name.trim()) errors.name = true;
    if (!formData.email.trim()) errors.email = true;
    if (!formData.company.trim()) errors.company = true;
    if (!formData.role.trim()) errors.role = true;
    if (!formData.linkedin.trim()) errors.linkedin = true;
    if (!formData.planning_experience.trim()) errors.planning_experience = true;
    if (!formData.venues_toured || formData.venues_toured <= 0) errors.venues_toured = true;
    if (!formData.events_planned || formData.events_planned <= 0) errors.events_planned = true;
    if (!formData.willing_to_review || formData.willing_to_review <= 0) errors.willing_to_review = true;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email.trim() && !emailRegex.test(formData.email.trim())) {
      errors.email = true;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      setSubmitStatus("error");
      setSubmitMessage("Please fill in all required fields correctly.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    try {
      const response = await fetch("/api/community/request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        setSubmitMessage("Thank you! Your request has been submitted successfully. We'll be in touch soon.");
        setShowConfirmation(true);
        setFormData({
          name: "",
          email: "",
          company: "",
          role: "",
          planning_experience: "",
          linkedin: "",
          venues_toured: 0,
          events_planned: 0,
          willing_to_review: 0,
        });
        setValidationErrors({});
      } else {
        setSubmitStatus("error");
        setSubmitMessage(result.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
      setSubmitMessage("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: string, value: string | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value ?? 0 }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  useEffect(() => {
    const allValid =
      formData.name.trim() &&
      formData.email.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) &&
      formData.company.trim() &&
      formData.role.trim() &&
      formData.linkedin.trim() &&
      formData.planning_experience.trim() &&
      formData.venues_toured > 0 &&
      formData.events_planned > 0 &&
      formData.willing_to_review > 0;

    if (submitStatus === "error" && submitMessage === "Please fill in all required fields correctly." && allValid) {
      setSubmitStatus("idle");
      setSubmitMessage("");
    }
  }, [
    formData.name,
    formData.email,
    formData.company,
    formData.role,
    formData.linkedin,
    formData.planning_experience,
    formData.venues_toured,
    formData.events_planned,
    formData.willing_to_review,
    submitStatus,
    submitMessage,
  ]);

  return (
    <main className="container-page">
      {/* Hero Section */}
      <section className="container-top-form">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          {/* Left Content */}
          <div className="flex flex-col gap-8 mb-8 md:mb-0">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Venue Collective</h1>
              <p className="text-body-lg text-landing-secondary max-w-530 font-semibold">
                A new community for event planners, by event planners. Built to save your hard-earned venue intel.
              </p>

              <div className="space-y-3">
                <h3 className="text-tag-xl text-landing-primary font-semibold">
                  Why This Changes Everything for Planners
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Your notes stay with you:</strong> Switch jobs without losing years of venue intel
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Real event data, not fluff:</strong> WiFi speeds, floorplan quirks, F&B realities that
                      Google won't tell you
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Decide faster:</strong> See how venues actually performed for events like yours
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Never start from scratch:</strong> Import and enrich your old venue lists in minutes
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Get rewarded:</strong> Earn FAM trips, invites, and industry cred for sharing
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Trusted, sales-free community:</strong> Advice from vetted planners, not vendor pitches
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Boost your value:</strong> Deliver better venue options instantly and look like a pro
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-600 mt-1">•</span>
                    <span className="text-body text-landing-secondary">
                      <strong>Meet AVA, your AI assistant:</strong> Just tell her about your venue experience and she'll
                      capture all the details, no typing required
                    </span>
                  </li>
                </ul>
              </div>

              <p className="text-body text-landing-secondary max-w-530 font-semibold">
                Join our first user cohort and get free lifetime access while shaping the platform with your ideas!
              </p>

              {/* Video Section */}
              <div className="mt-6 bg-violet-50 p-5 rounded-xl">
                <h3 className="text-tag-md text-landing-primary font-semibold mb-3">See How It Works</h3>
                <div className="w-full aspect-video rounded-lg overflow-hidden shadow-md">
                  <iframe
                    src="https://www.youtube.com/embed/z3whPTpNvOQ"
                    title="Venue Collective Demo"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="flex items-start justify-center">
            <motion.div
              className="w-full max-w-md"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-h4 text-landing-primary mb-0 sm:mb-8">Request to Join the Community</h3>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Full Name*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: John Doe"
                      value={formData.name}
                      setValue={(value) => handleInputChange("name", value)}
                      validator={!validationErrors.name}
                    />
                    {validationErrors.name && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        This field is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Work Email*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: john@company.com"
                      value={formData.email}
                      setValue={(value) => handleInputChange("email", value)}
                      validator={!validationErrors.email}
                    />
                    {validationErrors.email && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        {formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
                          ? "Please enter a valid email."
                          : "This field is required."}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Company*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: Acme Corp"
                      value={formData.company}
                      setValue={(value) => handleInputChange("company", value)}
                      validator={!validationErrors.company}
                    />
                    {validationErrors.company && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        This field is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Role/Title*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: Event Manager"
                      value={formData.role}
                      setValue={(value) => handleInputChange("role", value)}
                      validator={!validationErrors.role}
                    />
                    {validationErrors.role && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        This field is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">LinkedIn*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="linkedin.com/in/your-profile"
                      value={formData.linkedin}
                      setValue={(value) => handleInputChange("linkedin", value)}
                      validator={!validationErrors.linkedin}
                    />
                    {validationErrors.linkedin && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        This field is required.
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                        How many venues have you site-toured in the past year?*
                      </label>
                      <WholeNumberInput
                        className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                        value={formData.venues_toured}
                        setValue={(value) => handleInputChange("venues_toured", value)}
                        validator={!validationErrors.venues_toured}
                      />
                      {validationErrors.venues_toured && (
                        <div className="text-red-500 text-sm mt-1" role="alert">
                          This field is required and must be greater than 0.
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                        How many events have you planned in the past year?*
                      </label>
                      <WholeNumberInput
                        className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                        value={formData.events_planned}
                        setValue={(value) => handleInputChange("events_planned", value)}
                        validator={!validationErrors.events_planned}
                      />
                      {validationErrors.events_planned && (
                        <div className="text-red-500 text-sm mt-1" role="alert">
                          This field is required and must be greater than 0.
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                        How many venues are you willing to review per month?*
                      </label>
                      <WholeNumberInput
                        className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                        value={formData.willing_to_review}
                        setValue={(value) => handleInputChange("willing_to_review", value)}
                        validator={!validationErrors.willing_to_review}
                      />
                      {validationErrors.willing_to_review && (
                        <div className="text-red-500 text-sm mt-1" role="alert">
                          This field is required and must be greater than 0.
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                        Tell us about your event planning experience*
                      </label>
                      <textarea
                        className="w-full h-24 input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors resize-none"
                        placeholder="Brief description of your event planning background..."
                        value={formData.planning_experience}
                        onChange={(e) => handleInputChange("planning_experience", e.target.value)}
                      />
                      {validationErrors.planning_experience && (
                        <div className="text-red-500 text-sm mt-1" role="alert">
                          This field is required.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Message */}
                {submitStatus !== "idle" && (
                  <div
                    className={`p-3 rounded-md text-sm ${
                      submitStatus === "success"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {submitMessage}
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-2">
                  <Button variant="primary" size="md" onClick={handleSubmit} className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Write my first review"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div
          className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={() => setShowConfirmation(false)}
        >
          <motion.div
            className="bg-white p-8 rounded-2xl max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center space-y-4">
              <div className="text-4xl">🎉</div>
              <h2 className="text-landing-primary text-h3">Thank You!</h2>
              <p className="text-body text-landing-secondary">
                Your community request has been submitted successfully. We'll be in touch soon!
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  setShowConfirmation(false);
                  router.push("/venue-collective/review");
                }}
              >
                Start Writing Your Review
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* What You Get Section */}
      <section className="py-12 md:py-24">
        <div className="">
          <div className="text-center flex flex-col gap-6 mb-16">
            <h2 className="text-h2 text-landing-primary">What You'll Get:</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="rounded-xl bg-landing-primary border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex flex-col"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 p-3 rounded-lg bg-violet-50 text-violet-600">{feature.icon}</div>
                  <div>
                    <h3 className="text-tag-md text-landing-primary mb-3">{feature.title}</h3>
                    <p className="text-body text-landing-secondary leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Purpose Section */}
      <section className="py-12 md:py-24">
        <div className="">
          <div className="text-center flex flex-col gap-6 mb-16">
            <h2 className="text-h2 text-landing-primary max-w-[700px] mx-auto">A space for professional planners</h2>
          </div>

          <div className="space-y-20">
            {/* Section 1 - Left Content, Right Visual */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-16 w-full">
              <div className="w-full md:flex-1 flex flex-col gap-3 md:gap-4 order-2 md:order-1">
                <h3 className="text-h3 text-landing-primary max-w-530">
                  Share honest feedback on venues, vendors, and experiences
                </h3>
                <p className="text-body-lg text-landing-secondary max-w-530">
                  Browse real-world insights from other corporate teams who've actually used these venues. No more
                  relying on outdated websites or generic reviews.
                </p>
              </div>
              <div className="w-full md:flex-1 flex justify-center order-1 md:order-2">
                <img
                  src={communityGraphic1.src}
                  alt="Community"
                  className="w-full h-full object-cover w-full h-64 md:h-96 rounded-2xl bg-landing-secondary min-h-[256px]"
                />
              </div>
            </div>

            {/* Section 2 - Right Content, Left Visual */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-16 w-full">
              <div className="w-full md:flex-1 flex justify-center order-1">
                <img
                  src={communityGraphic2.src}
                  alt="Community"
                  className="w-full h-full object-cover w-full h-64 md:h-96 rounded-2xl bg-landing-secondary min-h-[256px]"
                />
              </div>
              <div className="w-full md:flex-1 flex flex-col gap-3 md:gap-4 order-2">
                <h3 className="text-h3 text-landing-primary max-w-530">
                  Import and organize your favorite venues in one place
                </h3>
                <p className="text-body-lg text-landing-secondary max-w-530">
                  Upload your own venue spreadsheet and we'll help clean, match, and enrich it with our database. Keep
                  your team's institutional knowledge organized and accessible.
                </p>
              </div>
            </div>

            {/* Section 3 - Left Content, Right Visual */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-16 w-full">
              <div className="w-full md:flex-1 flex flex-col gap-3 md:gap-4 order-2 md:order-1">
                <h3 className="text-h3 text-landing-primary max-w-530">
                  Ask questions and swap tips with fellow planners
                </h3>
                <p className="text-body-lg text-landing-secondary max-w-530">
                  Whether you're organizing a 20-person offsite or a 500-person summit, you shouldn't have to start from
                  scratch every time. Learn from others who've been there.
                </p>
              </div>
              <div className="w-full md:flex-1 flex justify-center order-1 md:order-2">
                <img
                  src={communityGraphic3.src}
                  alt="Community"
                  className="w-full h-full object-cover w-full h-64 md:h-96 rounded-2xl bg-landing-secondary min-h-[256px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-12 md:py-24">
        <div className="">
          <div className="text-center flex flex-col gap-6 mb-16">
            <h2 className="text-h2 text-landing-primary">Who This Is For:</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="rounded-xl border-2 border-violet-100 p-4 sm:p-5 md:p-6 hover:border-violet-300 transition-all duration-300 hover:shadow-lg flex items-center"
              >
                <div className="flex-shrink-0 p-3 rounded-lg bg-violet-50 text-violet-600">{audience.icon}</div>
                <p className="text-body text-landing-primary ml-4">{audience.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-12 md:py-24">
        <div className=" rounded-2xl p-12">
          <div className="text-center mb-8">
            <h2 className="text-h2 text-landing-primary mb-6">Why Join?</h2>
            <p className="text-body-lg text-landing-secondary mb-8">
              Because planners deserve better data—and a better way to share it.
            </p>
            <p className="text-body text-landing-secondary mb-8">
              By contributing, you'll help build a living, breathing "Wikipedia for Venues" that grows smarter with
              every event.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 border shadow-sm max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-4xl mb-4">💡</div>
              <p className="text-tag-xl text-landing-primary mb-6 italic">
                "I wish I had known that before we booked."
              </p>
              <p className="text-body text-landing-secondary">
                Get early access and help shape the future of event planning.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
